'use client'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface SnapCarouselProps {
  /** List element semantics — 'ol' for ordered step lists */
  as?: 'ul' | 'ol'
  /** Accessible name for the scrollable list */
  label: string
  /** Classes for the scroller: mobile flex/snap + desktop grid overrides */
  listClassName: string
  /** Classes for each slide wrapper (mobile width + desktop resets) */
  itemClassName: string
  /** One accessible name per slide, used by the dot buttons */
  slideLabels: string[]
  /** Breakpoint class that hides the dots where the grid takes over */
  dotsClassName: string
  children: React.ReactNode[]
}

/**
 * The site's snap carousel: swipeable scroll-snap list on mobile with
 * tracking page dots, a plain grid at the desktop breakpoint (pass the grid
 * classes in listClassName and hide the dots via dotsClassName). Dot pattern
 * established by the teaching-card gallery: 44px-tall touch targets,
 * aria-current on the active dot, reduced-motion-aware scrolling.
 */
export function SnapCarousel({
  as: List = 'ul',
  label,
  listClassName,
  itemClassName,
  slideLabels,
  dotsClassName,
  children,
}: SnapCarouselProps) {
  const scrollerRef = useRef<HTMLElement | null>(null)
  const [active, setActive] = useState<number>(0)

  const setScroller = (
    node: HTMLUListElement | HTMLOListElement | null
  ): void => {
    scrollerRef.current = node
  }

  const onScroll = (): void => {
    const node = scrollerRef.current
    if (!node) return
    const center = node.scrollLeft + node.clientWidth / 2
    const items = Array.from(node.children) as HTMLElement[]
    let best = 0
    let bestDistance = Infinity
    items.forEach((item, index) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2
      const distance = Math.abs(itemCenter - center)
      if (distance < bestDistance) {
        bestDistance = distance
        best = index
      }
    })
    setActive(best)
  }

  const scrollTo = (index: number): void => {
    const item = scrollerRef.current?.children[index] as HTMLElement | undefined
    if (!item) return
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    item.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }

  return (
    <div>
      <List
        ref={setScroller}
        aria-label={label}
        onScroll={onScroll}
        className={cn(
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          listClassName
        )}
      >
        {children.map((child, index) => (
          <li key={slideLabels[index] ?? index} className={itemClassName}>
            {child}
          </li>
        ))}
      </List>

      {children.length > 1 && (
        <div className={cn('mt-3 flex justify-center', dotsClassName)}>
          {slideLabels.map((slideLabel, index) => (
            <button
              key={slideLabel}
              type="button"
              onClick={() => scrollTo(index)}
              aria-label={`Show ${slideLabel}`}
              aria-current={active === index}
              className="flex h-11 w-9 items-center justify-center"
            >
              <span
                aria-hidden
                className={cn(
                  'h-2 rounded-full transition-all duration-300 motion-reduce:transition-none',
                  active === index ? 'bg-primary w-6' : 'bg-border w-2'
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
